import {
	Dialog,
	Form,
	FormItem,
	RadioButtonGroup,
	renderReactDialog,
	Select,
	SelectItem,
	TextArea,
	TextInput,
	ValidationError,
	InputLike,
	RadioButtonGroupItem,
	AlertDialog,
	Toolbar
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { AlertManager, useAlerts, useUnmountedCancellation } from "client/Hooks";
import { createValidationConfiguration, InputValidationResult, InputValidationResultKind } from "client/Validation";
import { inline, isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { ReactElement, useEffect, useMemo, useState } from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { reloadCurrentPage } from "client_legacy/ndaw/navigator";
import { PermissionsMode } from "client/Api/SharePointSettings/sharepointSettings";
import {
	createSubsite,
	CreateSubsiteModel,
	getNewSubsiteAvailableTemplates,
	getNewSubsiteViewModel,
	NewSubsiteViewModel,
	SiteTemplateListItem,
} from "client/Api/Documents/SharePoint/site";
import styles from "./NewSubsiteDialog.module.less";

const UNSET_LANGUAGE_ID: number = -1;

const permissionRadioGroupOptions: RadioButtonGroupItem<PermissionsMode>[] = [
	{
		text: localize("_UseSamePermissionsAsParentSite"),
		value: PermissionsMode.Inherit,
	}, {
		text: localize("_UseUniquePermissions"),
		value: PermissionsMode.Unique,
	}
];

const validationConfiguration = createValidationConfiguration<CreateSubsiteModel>();
validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forScalar("url")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		inline((value)=>{
			if(/^_/.test(value)){
				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_CantBeginWithUnderscore")
				};
			}
			return {
				kind: InputValidationResultKind.Valid
			};
		})
	);

validationConfiguration.forScalar("language")
	.add(
		inline((value)=>{
			if(value === UNSET_LANGUAGE_ID){
				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_InvalidInput")
				};
			}
			return {
				kind: InputValidationResultKind.Valid
			};
		})
	);

export type NewSubsiteDialogProps = {
	onClose: () => void;
	siteUrl: string;
};

export const NewSubsiteDialog = (
	props: NewSubsiteDialogProps
): ReactElement => {
	const { onClose, siteUrl: siteUrlFromProps } = props;
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [hadErrorLoading, setHadErrorLoading] = useState<boolean>(false);
	const [newSubsiteAvailableTemplates, setNewSubsiteAvailableTemplates] =
		useState<SiteTemplateListItem[]>([]);
	const [newSubsiteViewModel, setNewSubsiteViewModel] =
		useState<NewSubsiteViewModel | null>(null);

	const [formValue, setFormValue] = useState<CreateSubsiteModel>({
		name: "",
		url: "",
		description: "",
		language: UNSET_LANGUAGE_ID,
		template: "",
		permissionsMode: PermissionsMode.Inherit,
		siteUrl: siteUrlFromProps
	});

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const submitHandler = async (): Promise<void> => {
		try {
			setIsBusy(true);

			const validationResult: InputValidationResult =
				await formHandler.validation.validate(globalCancellation);
			if (validationResult.kind === InputValidationResultKind.Invalid) {
				setIsBusy(false);
				return;
			}

			await createSubsite(formValue, globalCancellation);

			reloadCurrentPage();
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			setIsBusy(false);

			if (formHandler.validation.tryProcessServerError(e)) {
				return;
			}

			alertManager.showError(localize("_CouldNotCreateSubsiteDueToUnkownReason"));
		}
	};

	useEffect(
		() => {
			async function init(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					const newSubsiteViewModel: NewSubsiteViewModel = await getNewSubsiteViewModel(
						siteUrlFromProps,
						signal
					);
					setIsBusy(false);
					setNewSubsiteViewModel(newSubsiteViewModel);
					setFormValue((formValue) => (
						{
							...formValue,
							language: newSubsiteViewModel.defaultLanguage,
							description: newSubsiteViewModel.defaultDescription,
							permissionsMode: newSubsiteViewModel.defaultPermissionsMode
						})
					);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setHadErrorLoading(true);
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			init(cancellation.signal);

			return () => {
				cancellation.abort();
			};
		},
		[siteUrlFromProps]
	);

	const formHandler: IFormHandler<CreateSubsiteModel> = useFormHandler(
		formValue,
		setFormValue,
		validationConfiguration
	);

	const languageOptions: SelectItem<number>[] = useMemo(
		() => newSubsiteViewModel?.availableLanguages
			.map(({ id, name }) => ({ value: id, text: name })) || [],
		[newSubsiteViewModel?.availableLanguages]
	);

	const templatesOptions: SelectItem<string>[] = useMemo(
		() => newSubsiteAvailableTemplates
			.map(({ title, name }) => ({ value: name, text: title })) || [],
		[newSubsiteAvailableTemplates]
	);

	const siteUrl: string = useMemo(() => {
		if (!newSubsiteViewModel?.siteUrl) {
			return "";
		}
		if (!newSubsiteViewModel?.siteUrl.endsWith("/")) {
			return newSubsiteViewModel?.siteUrl + "/";
		}
		return newSubsiteViewModel?.siteUrl;
	}, [newSubsiteViewModel?.siteUrl]);

	useEffect(
		()=>{
			async function fetchTemplates(): Promise<void> {
				try {
					if (formValue.language === newSubsiteViewModel?.defaultLanguage) {
						setNewSubsiteAvailableTemplates(newSubsiteViewModel?.availableTemplates);
						setFormValue((formValue) => ({
							...formValue,
							template: newSubsiteViewModel?.availableTemplates[0].name,
						}));
						return;
					}

					if (formValue.language !== UNSET_LANGUAGE_ID) {
						const newSubsiteAvailableTemplates: SiteTemplateListItem[] =
							await getNewSubsiteAvailableTemplates(
								siteUrlFromProps,
								formValue.language,
								globalCancellation
							);
						setNewSubsiteAvailableTemplates(newSubsiteAvailableTemplates);
						setFormValue((formValue) => ({
							...formValue,
							template: newSubsiteAvailableTemplates[0].name,
						}));
					}
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
				}
			}

			fetchTemplates();
		},
		[
			formValue.language,
			globalCancellation,
			newSubsiteViewModel,
			siteUrlFromProps
		]
	);

	if (hadErrorLoading){
		return (
			<AlertDialog
				onClose={onClose}
				title={localize("_UnknownError")}
				confirmText={localize("_Close")}
				trackingId="new-subsite-dialog-unknown-error"
			>
				{localize("_ItIsCurrentlyNotPossibleToCreateANewSubsite")}
			</AlertDialog>
		);
	}

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="new-subsite-dialog"
		>
			<Dialog.Header
				title={localize("_NewSubsite")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_Name")}>
						<TextInput
							{...formHandler.for("name").attachToTextInput()}
						/>
						<ValidationError
							{...formHandler.for("name").attachToValidationError()}
						/>
					</FormItem>
					{newSubsiteViewModel?.canUserChangeDescription && (
						<FormItem label={localize("_Description")}>
							<TextArea
								{...formHandler.for("description").attachToTextArea()}
							/>
							<ValidationError
								{...formHandler.for("description").attachToValidationError()}
							/>
						</FormItem>
					)}
					<FormItem label={localize("_Url")}>
						<div className={styles.urlControlContainer}>
							<div className={styles.subSitePart}>
								<InputLike
									value={siteUrl}
									isInvalid={formHandler.for("url").validation.getValidationState().kind === InputValidationResultKind.Invalid}
									trackingId="site-url"
									attachedOn="right"
								/>
							</div>
							<TextInput
								{...formHandler.for("url").attachToTextInput()}
								attachedOn="left"
							/>
						</div>
						<ValidationError
							{...formHandler.for("url").attachToValidationError()}
						/>
					</FormItem>
					{newSubsiteViewModel?.canUserChangeLanguage && (
						<FormItem label={localize("_Language")}>
							<Select
								{...formHandler.for("language").attachToSelect()}
								options={languageOptions}
								menuTitle={localize("_Language")}
							/>
							<ValidationError
								{...formHandler.for("language").attachToValidationError()}
							/>
						</FormItem>
					)}
					<FormItem label={localize("_Template")}>
						<Select
							{...formHandler.for("template").attachToSelect()}
							options={templatesOptions}
							menuTitle={localize("_Template")}
						/>
						<ValidationError
							{...formHandler.for("template").attachToValidationError()}
						/>
					</FormItem>
					{newSubsiteViewModel?.canUserChangePermissionsMode && (
						<FormItem
							label={localize("_Permissions")}
							isForGroup
						>
							<RadioButtonGroup
								{...formHandler.for("permissionsMode").attachToRadioButtonGroup()}
								options={permissionRadioGroupOptions}
							/>
							<ValidationError
								{...formHandler.for("permissionsMode").attachToValidationError()}
							/>
						</FormItem>
					)}
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Done")}
						onClick={submitHandler}
						trackingId="done"
						/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

NewSubsiteDialog.displayName = "NewSubsiteDialog";

export function openNewSubsiteDialog(
	props: Omit<NewSubsiteDialogProps, "onClose">
): void {
	renderReactDialog((onClose) => (
		<NewSubsiteDialog {...props} onClose={onClose} />
	));
}
